import { formatDate } from '@karehero/helpers/datetime';
import type { CareProfileCategory } from '@/careProfile';
import type { ExtractedFact, ExtractedFactMappingOption } from './index';

const formatBoolean = (value: unknown): string => (value ? 'Yes' : 'No');

const formatDateValue = (value: unknown): string => {
  if (typeof value !== 'string') return String(value);
  const iso = value.includes('T') ? value : `${value}T00:00:00`;
  return formatDate(iso, { omitDay: true });
};

const fieldTypeFormatters: Record<string, (value: unknown) => string> = {
  boolean: formatBoolean,
  date: formatDateValue,
};

/** The labels of the options a value names, one or several; a value that isn't one stays as it is. */
export const formatOptionValue = (
  value: unknown,
  options: ExtractedFactMappingOption[],
): string => {
  const ids = Array.isArray(value) ? value : [value];
  return ids
    .map(
      (id) => options.find((option) => option.id === id)?.label ?? String(id),
    )
    .join(', ');
};

export const formatFactValue = (fact: ExtractedFact): string => {
  const mapping = fact.mappings[0];
  if (mapping?.options?.length) {
    return formatOptionValue(fact.factValue, mapping.options);
  }
  const fieldType = mapping?.fieldType;
  const formatter = fieldType ? fieldTypeFormatters[fieldType] : undefined;
  return formatter ? formatter(fact.factValue) : String(fact.factValue);
};

export const interpolateCareRecipient = (
  text: string,
  careRecipientName?: string,
): string =>
  careRecipientName
    ? text.replace(/\{careRecipient\}/g, careRecipientName)
    : text.replace(/\{careRecipient\}/g, 'your loved one');

export const buildQuestionFormMap = (
  categories: CareProfileCategory[],
): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  for (const cat of categories) {
    for (const sub of cat.careProfileSubCategories) {
      for (const form of sub.careProfileForms) {
        const path = `${cat.title}: ${form.title}`;
        for (const q of form.careProfileQuestions) {
          (map[q.id] ??= []).push(path);
        }
      }
    }
  }
  return map;
};
